import { Box, Chip, Grid, Typography } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import React from "react";
import { tokens, eyebrowSx, numericSx } from "../../../theme";
import { absent } from "../../../utils/missing";
import MiniSpinner from "../miniSpinner/miniSpinner";

/**
 * The KPI/stat tile inside an analytics card — the SectionCard trick one level
 * down: ONE component, and the structural difference between themes lives
 * here, keyed on `tokens.cardChrome.variant`, never in the views.
 *
 * Legacy ("banner") reproduces the original hand-rolled Grid markup for each
 * shape byte-for-byte from tokens; "plain" renders the refreshed kpiCard /
 * sidebar-mui preview conventions (eyebrowSx label, tabular-nums value, Chip
 * delta). The shape is picked from the props, not a variant enum:
 *
 *   column               → big value + label (+ delta band)          [GF tiles]
 *   column + eyebrow     → bold label above a coloured value         [TX industry tiles]
 *   column + accent      → coloured accent tile (icon, white ink)    [dashboard Retention/NPS]
 *   row + icon           → dashboard icon row-tile (+ delta band)
 *   row + icon + accent  → at-risk stat row (icon chip, white ink)
 *   row (bare)           → "label: value" mini tile                  [NPS / Retention Leads minis]
 *
 * Delta semantics are the original's, exactly: keyed strictly on `> 0`, so
 * zero styles down and gets no "+"; `null` renders the missing-data dash in a
 * down-styled band. Value formatting ($, %, M, toFixed) stays at call sites —
 * zero is a value.
 */
export interface StatTileProps {
  /** Preformatted — the caller keeps "$ ", "%", "M", toFixed. */
  value: React.ReactNode;
  /** Text under the value ("Transactions", "Current") — or before it in a row mini ("Lost:"). */
  label?: React.ReactNode;
  /** Bold text ABOVE the value ("MARKET SHARE"); the h6 title on accent tiles. */
  eyebrow?: string;
  /** Small supporting line ("if share increase 1%", "3 month average"). */
  caption?: string;
  /**
   * Renders the trend band/chip. Undefined → none; null → dash in down
   * styling (the isNumeric-guarded "Projected" tile).
   */
  delta?: number | null;
  /** Token value for the value ink (TX tile colours, the at-risk red). */
  valueColor?: string;
  /** Tile background token. Default tokens.cardChrome.tileBg. */
  bg?: string;
  /** Fully-formed icon block (the 60×60 png Grids, the Percent IconButton). */
  icon?: React.ReactNode;
  /** Tinted square behind `icon` (tokens.tile.chip*Bg). */
  iconBg?: string;
  orientation?: "column" | "row";
  /** Value scale: lg h3 / md h4 / sm h5 / xs body. */
  size?: "lg" | "md" | "sm" | "xs";
  /** Legacy column geometry: GF's centered "1%" trio vs left-aligned "10px" pair. */
  align?: "center" | "left";
  /** Coloured-tile mode: radius 10, onAccent ink, hover ring when clickable. */
  accent?: boolean;
  /** Replaces the value with MiniSpinner. */
  loading?: boolean;
  onClick?: () => void;
}

const LEGACY_VALUE_VARIANT = { lg: "h3", md: "h4", sm: "h5" } as const;
const PLAIN_VALUE_PX = { lg: 32, md: 26, sm: 20, xs: 15 } as const;

const formatDelta = (delta: number) =>
  delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);

/** The original 40px trend band, verbatim: bg/icon/ink keyed strictly on > 0. */
const LegacyDeltaBand = ({ delta }: { delta: number | null }) => {
  const up = delta !== null && delta > 0;
  return (
    <Grid
      sx={{
        display: "flex",
        flexDirection: "row",
        backgroundColor: up ? tokens.chart.delta.upBg : tokens.chart.delta.downBg,
        height: "40px",
        alignItems: "center",
        padding: "10px",
        gap: 1,
        width: "100%",
        justifyContent: "center",
      }}
    >
      {up ? (
        <TrendingUpIcon sx={{ color: tokens.chart.delta.up }} />
      ) : (
        <TrendingDownIcon sx={{ color: tokens.chart.delta.down }} />
      )}
      <Typography
        variant="h4"
        sx={{
          fontWeight: "bold",
          color: up ? tokens.chart.delta.up : tokens.chart.delta.down,
        }}
      >
        {delta === null ? absent : <>{formatDelta(delta)}%</>}
      </Typography>
    </Grid>
  );
};

const LegacyValue = ({
  value,
  size,
  valueColor,
  loading,
  spinnerHeight,
}: {
  value: React.ReactNode;
  size: "lg" | "md" | "sm" | "xs";
  valueColor?: string;
  loading?: boolean;
  spinnerHeight?: string;
}) => {
  if (loading) {
    return (
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...(spinnerHeight && { height: spinnerHeight }),
        }}
      >
        <MiniSpinner />
      </Grid>
    );
  }
  if (size === "xs") {
    return (
      <Typography sx={{ fontWeight: "bold", ...(valueColor && { color: valueColor }) }}>
        {value}
      </Typography>
    );
  }
  return (
    <Typography
      variant={LEGACY_VALUE_VARIANT[size]}
      sx={{ fontWeight: "bold", ...(valueColor && { color: valueColor }) }}
    >
      {value}
    </Typography>
  );
};

const LegacyStatTile = (props: StatTileProps) => {
  const {
    value, label, eyebrow, caption, delta, valueColor, bg, icon, iconBg,
    orientation = "column", size = "md", align = "center", accent, loading, onClick,
  } = props;
  const background = bg ?? tokens.cardChrome.tileBg;

  if (orientation === "row") {
    if (accent) {
      // At-risk stat row: icon chip on a coloured band, white ink.
      return (
        <Grid
          container
          item
          xs={12}
          sx={{ backgroundColor: background, borderRadius: "10px", display: "flex", alignItems: "center" }}
          onClick={onClick}
        >
          <Grid item xs={4} sx={{ display: "flex", justifyContent: "center" }}>
            {icon}
          </Grid>
          <Grid>
            <LegacyValue
              value={value}
              size={size}
              valueColor={valueColor ?? tokens.tile.onAccent}
              loading={loading}
            />
            <Typography sx={{ color: tokens.tile.onAccent }}>{label}</Typography>
          </Grid>
        </Grid>
      );
    }
    if (icon) {
      // Dashboard icon row-tile: tinted icon square, value + label, delta band.
      return (
        <Grid
          container
          item
          xs={12}
          sx={{ backgroundColor: background, padding: "10px", borderRadius: "5px" }}
          onClick={onClick}
        >
          <Grid
            container
            item
            xs={2}
            sx={{ justifyContent: "center", backgroundColor: iconBg, borderRadius: "5px" }}
          >
            {icon}
          </Grid>
          <Grid
            container
            item
            xs={5}
            sx={{ padding: "0px 0px 0px 20px", display: "flex", flexDirection: "column" }}
          >
            <LegacyValue value={value} size={size} valueColor={valueColor} loading={loading} />
            <Typography>{label}</Typography>
          </Grid>
          {delta !== undefined && (
            <Grid
              container
              item
              xs={5}
              sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <LegacyDeltaBand delta={delta} />
            </Grid>
          )}
        </Grid>
      );
    }
    // "label: value" mini tile.
    return (
      <Grid
        container
        item
        xs={12}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: background,
          padding: size === "xs" ? "5px" : "10px",
          borderRadius: "5px",
          gap: 1,
        }}
        onClick={onClick}
      >
        <Typography>{label}</Typography>
        {loading ? (
          <MiniSpinner />
        ) : (
          <LegacyValue value={value} size={size} valueColor={valueColor} />
        )}
      </Grid>
    );
  }

  if (accent) {
    // Coloured accent tile: icon, h6 title, big white value, caption.
    return (
      <Grid
        item
        xs={12}
        sx={{
          backgroundColor: background,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          borderRadius: "10px",
          padding: "10px",
          ...(onClick && {
            cursor: "pointer",
            "&:hover": { border: `2px solid ${tokens.color.primary}` },
          }),
        }}
        onClick={onClick}
      >
        {icon}
        <Typography variant="h6" sx={{ color: tokens.tile.onAccent }}>
          {eyebrow}
        </Typography>
        <LegacyValue
          value={value}
          size={size}
          valueColor={valueColor ?? tokens.tile.onAccent}
          loading={loading}
          spinnerHeight="40px"
        />
        <Typography sx={{ color: tokens.tile.onAccent }}>{caption}</Typography>
      </Grid>
    );
  }

  if (eyebrow) {
    // Bold label above a coloured value (TX industry tiles).
    return (
      <Grid
        item
        xs={12}
        sx={{
          backgroundColor: background,
          paddingTop: "10px",
          borderRadius: "5px",
          justifyContent: "center",
        }}
        onClick={onClick}
      >
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Typography sx={{ fontWeight: "bold" }}>{eyebrow}</Typography>
          {caption && (
            <Typography sx={{ fontSize: "12px", color: tokens.tile.caption }}>
              {caption}
            </Typography>
          )}
          <LegacyValue value={value} size={size} valueColor={valueColor} loading={loading} />
        </Grid>
      </Grid>
    );
  }

  if (delta === undefined && size === "sm") {
    // Compact no-delta tile (GF "Confidence").
    return (
      <Grid
        container
        item
        xs={12}
        sx={{
          backgroundColor: background,
          padding: "10px",
          borderRadius: "5px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
        onClick={onClick}
      >
        <LegacyValue value={value} size={size} valueColor={valueColor} loading={loading} />
        <Typography>{label}</Typography>
      </Grid>
    );
  }

  // Big value + label + delta band (GF Growth Rate / Forecast tiles).
  const centered = align === "center";
  return (
    <Grid
      container
      item
      xs={12}
      sx={{
        backgroundColor: background,
        padding: centered ? "1%" : "10px",
        borderRadius: "5px",
        ...(centered && { display: "flex", alignItems: "center", justifyContent: "center" }),
      }}
      onClick={onClick}
    >
      <Grid
        container
        item
        xs={12}
        sx={{
          padding: centered ? "0px 0px 0px 10px" : "0px 0px 0px 5px",
          display: "flex",
          flexDirection: "column",
          ...(centered && { alignContent: "center", justifyContent: "center" }),
        }}
      >
        <LegacyValue value={value} size={size} valueColor={valueColor} loading={loading} />
        <Typography>{label}</Typography>
      </Grid>
      {delta !== undefined && (
        <Grid container item xs={12} sx={{ display: "flex" }}>
          <LegacyDeltaBand delta={delta} />
        </Grid>
      )}
    </Grid>
  );
};

const PlainDeltaChip = ({ delta }: { delta: number | null }) => {
  const up = delta !== null && delta > 0;
  return (
    <Chip
      size="small"
      icon={
        up ? (
          <TrendingUpIcon sx={{ fontSize: 13 }} />
        ) : (
          <TrendingDownIcon sx={{ fontSize: 13 }} />
        )
      }
      label={delta === null ? absent : `${formatDelta(delta)}%`}
      sx={{
        bgcolor: up ? tokens.color.positive.bg : tokens.color.negative.bg,
        color: up ? tokens.color.positive.text : tokens.color.negative.text,
        fontWeight: 500,
        "& .MuiChip-icon": { color: "inherit" },
      }}
    />
  );
};

const PlainStatTile = (props: StatTileProps) => {
  const {
    value, label, eyebrow, caption, delta, valueColor, bg, icon, iconBg,
    orientation = "column", size = "md", align = "center", accent, loading, onClick,
  } = props;
  const isRow = orientation === "row";
  const mini = isRow && !icon && !accent;
  const ink = accent ? tokens.tile.onAccent : tokens.color.ink;
  const sub = accent ? tokens.tile.onAccentMuted : tokens.color.muted;

  const valueNode = loading ? (
    <MiniSpinner />
  ) : (
    <Typography
      sx={{
        ...numericSx,
        fontSize: PLAIN_VALUE_PX[size],
        fontWeight: 600,
        lineHeight: 1.15,
        letterSpacing: "-0.02em",
        color: valueColor ?? ink,
      }}
    >
      {value}
    </Typography>
  );

  return (
    <Box
      onClick={onClick}
      sx={{
        width: "100%",
        backgroundColor: bg ?? tokens.cardChrome.tileBg,
        borderRadius: `${tokens.cardChrome.radius}px`,
        padding: mini ? "8px 12px" : "12px 14px",
        display: "flex",
        flexDirection: isRow ? "row" : "column",
        alignItems: isRow ? "center" : align === "left" ? "flex-start" : "center",
        ...(mini && { justifyContent: "center" }),
        gap: isRow ? 1.5 : 0.5,
        ...(onClick && {
          cursor: "pointer",
          transition: `box-shadow ${tokens.motion.base} ${tokens.motion.easing}`,
          "&:hover": { boxShadow: tokens.shadow.cardHover },
        }),
      }}
    >
      {icon && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: iconBg,
            borderRadius: "8px",
            flex: "none",
          }}
        >
          {icon}
        </Box>
      )}
      {mini ? (
        <>
          {label && <Typography sx={{ fontSize: 13, color: sub }}>{label}</Typography>}
          {valueNode}
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: isRow || align === "left" ? "flex-start" : "center",
            gap: 0.25,
            minWidth: 0,
            ...(isRow && { flex: 1 }),
          }}
        >
          {eyebrow && (
            <Typography sx={{ ...eyebrowSx, ...(accent && { color: tokens.tile.onAccent }) }}>
              {eyebrow}
            </Typography>
          )}
          {valueNode}
          {label && <Typography sx={{ fontSize: 13, color: sub }}>{label}</Typography>}
          {caption && (
            <Typography sx={{ fontSize: 12, color: accent ? tokens.tile.onAccentMuted : tokens.color.faint }}>
              {caption}
            </Typography>
          )}
        </Box>
      )}
      {delta !== undefined && (
        <Box sx={isRow ? { flex: "none" } : { marginTop: 0.5 }}>
          <PlainDeltaChip delta={delta} />
        </Box>
      )}
    </Box>
  );
};

const StatTile = (props: StatTileProps) =>
  tokens.cardChrome.variant === "banner" ? (
    <LegacyStatTile {...props} />
  ) : (
    <PlainStatTile {...props} />
  );

export default StatTile;
